import * as Highcharts from "highcharts";
import React from "react";
import HighchartsReact from "highcharts-react-official";
import { ILessonsChartsInfo } from "ts/parsers/analytics";
import { lessonsTooltipFormatter } from "../../../../formatters/analytics";
import {
  centerSeriesBarTooltip,
  defaultColumnPlotOptions,
  defaultEmptyTitle,
  defaultIndividualUnitsSettings,
  defaultTooltip,
} from "../../../../utils/charts";
import colors from "../../../../styles/colors";
import { createGradient } from "../../../../formatters/analytics";

interface ILessonsChartProps {
  lessons: ILessonsChartsInfo[];
  showXAxis: boolean;
  maxStudents: number;
  isFilterActive?: boolean;
}

export const LessonsChart: React.FC<ILessonsChartProps> = ({ lessons, maxStudents, showXAxis, isFilterActive }) => (
  <HighchartsReact
    highcharts={Highcharts}
    options={lessonsChartOptions(lessons, maxStudents, showXAxis, isFilterActive)}
    containerProps={{ style: { width: "90%" } }}
  />
);

const lessonsChartOptions = (
  lessons: ILessonsChartsInfo[],
  maxStudents: number,
  showXAxis: boolean,
  isFilterActive?: boolean
): Highcharts.Options => ({
  chart: {
    backgroundColor: "transparent",
    type: "column",
  },
  plotOptions: {
    ...defaultColumnPlotOptions,
  },
  series: [
    {
      data: lessons.map((lesson: ILessonsChartsInfo) => lesson.students),
      name: "Students",
      /* tslint:disable-next-line */
      color: isFilterActive
        ? createGradient(colors.chartFilteredGray100, colors.chartFilteredGray70)
        : createGradient(colors.chartGreen100, colors.chartGreen70),
    } as Highcharts.SeriesBarOptions,
    isFilterActive &&
      ({
        data: lessons.map((lesson: ILessonsChartsInfo) => lesson.filtered),
        name: "Filtered",
        /* tslint:disable-next-line */
        color: createGradient(colors.chartGreen100, colors.chartGreen70),
      } as Highcharts.SeriesBarOptions),
  ].filter(Boolean) as Highcharts.SeriesBarOptions[],
  tooltip: {
    formatter() {
      const label = this.x!;
      const value = this.y!;
      const isFiltered = isFilterActive && this.series.name === "Filtered";
      const textColor = this.series.color as Highcharts.GradientColorObject;
      return lessonsTooltipFormatter(value, maxStudents, label, textColor.stops[0][1], isFiltered);
    },
    positioner(...params) {
      return centerSeriesBarTooltip(this.chart)(...params);
    },
    ...defaultTooltip,
  },
  xAxis: {
    categories: lessons.map((lesson: ILessonsChartsInfo) => lesson.name),
    gridLineColor: "transparent",
    labels: {
      formatter() {
        return showXAxis ? `<b>${String(this.value)}</b>` : "";
      },
      style: {
        fontSize: "1.4rem",
      },
    },
    lineColor: colors.chartAxisGrey100,
    lineWidth: showXAxis ? 3 : 0,
    opposite: true,
  },
  yAxis: {
    labels: {
      enabled: false,
    },
    max: maxStudents,
    ...defaultEmptyTitle,
  },
  ...defaultIndividualUnitsSettings,
});
